// CogniPrint Profile — Full type definition
// 8 dimensions: 4 free-tier (assessed in quick diagnostic), 4 paid-tier

import {
  DEPTH_TOLERANCE_BANDS,
  LOW_MEDIUM_HIGH_BANDS,
  bandLabel,
  isLearningAxisBalanced,
  isUncertain,
} from '../constants/confidence-bands';

export interface ContentFormatPreference {
  visual: number;       // 0-100, all weights sum to 100
  textual: number;
  auditory: number;
  interactive: number;
  video: number;
}

export interface LearningApproach {
  theoryApplication: number;  // 0-100 (0 = pure application-first, 100 = pure theory-first)
  reflectiveActive: number;   // 0-100 (0 = pure active, 100 = pure reflective)
}

export interface SequencingPreference {
  topDown: number;    // 0-100, all weights sum to 100
  bottomUp: number;
  modular: number;
}

export interface MotivationalDriver {
  mastery: number;      // 0-100, all weights sum to 100
  performance: number;
  social: number;
  curiosity: number;
}

export interface DimensionScore<T = number> {
  score: T;
  confidence: number;  // 0.0 - 1.0
}

/**
 * Where a dimension's score actually came from. The single most important field
 * on a profile: it is the difference between "we measured this" and "we made
 * this up because the payload was missing". See docs/intent/001-diagnostic-redesign.
 *
 * - `anchored`     scored deterministically from the fixed anchor items
 * - `inferred`     scored by the model from free-text or follow-up answers
 * - `behavioural`  updated from checkpoint performance / recalibration evidence
 * - `defaulted`    no usable evidence; a centre value stands in. NEVER a finding.
 * - `not_assessed` never presented to this learner. NEVER a finding.
 */
export type DimensionProvenance =
  | 'anchored'
  | 'inferred'
  | 'behavioural'
  | 'defaulted'
  | 'not_assessed';

/** The seven dimensions assessed in a diagnostic session. Prior knowledge is per-upload. */
export type DiagnosticDimensionKey =
  | 'contentFormat'
  | 'learningApproach'
  | 'depthTolerance'
  | 'sequencing'
  | 'infoDensity'
  | 'motivation'
  | 'viewpointRange';

export const DIAGNOSTIC_DIMENSION_KEYS: DiagnosticDimensionKey[] = [
  'contentFormat', 'learningApproach', 'depthTolerance', 'sequencing',
  'infoDensity', 'motivation', 'viewpointRange',
];

export const FREE_DIMENSION_KEYS: DiagnosticDimensionKey[] = [
  'contentFormat', 'learningApproach', 'depthTolerance', 'sequencing',
];

export const PAID_DIMENSION_KEYS: DiagnosticDimensionKey[] = [
  'infoDensity', 'motivation', 'viewpointRange',
];

/** A learner's answer to one fixed anchor item. Retained so reliability (Cronbach's
 *  alpha, test-retest) can be computed on a beta wave without re-running diagnostics. */
export interface AnchorItemResponse {
  itemId: string;
  anchorSetVersion: string;
  dimension: DiagnosticDimensionKey;
  optionId: string | null;   // null when skipped or unresolvable
  skipped: boolean;
  respondedAt: string;       // ISO timestamp
}

/** True on profiles written by the Admin Content Lab. Such a profile is test data
 *  and must never be rendered to a learner or treated as evidence. */
export const TEST_PROFILE_VERSION = 999;

export interface CogniPrintProfile {
  profileVersion: number;
  diagnosticType: 'quick' | 'full';
  userId: string;
  createdAt: string;      // ISO timestamp
  lastUpdated: string;    // ISO timestamp

  // Free tier dimensions (assessed in quick diagnostic)
  contentFormat: DimensionScore<ContentFormatPreference>;
  learningApproach: DimensionScore<LearningApproach>;
  depthTolerance: DimensionScore<number>;  // 0-100
  sequencing: DimensionScore<SequencingPreference>;

  // Paid tier dimensions (assessed in full diagnostic)
  infoDensity: DimensionScore<number> | null;       // 0-100, null if quick diagnostic only
  motivation: DimensionScore<MotivationalDriver> | null;
  viewpointRange: DimensionScore<number> | null;    // 0-100
  // priorKnowledge is per-upload, stored on content doc, NOT here

  // Meta
  questionsAsked: number;
  questionsCarriedForward: number;
  overallConfidence: number;          // 0.0 - 1.0
  archetypeMatch: string | null;     // e.g., "Visual Explorer", "Systematic Deep-Diver"
  adaptiveNotes: string;

  // Provenance and instrument metadata. Optional so profiles written before the
  // 2026-09-05 diagnostic redesign still parse; normalizeProfile() backfills them.
  dimensionProvenance?: Partial<Record<DiagnosticDimensionKey, DimensionProvenance>>;
  anchorSetVersion?: string | null;
  itemResponses?: AnchorItemResponse[];
  evidenceStatusVersion?: string;
  /** Written only by the Admin Content Lab. See TEST_PROFILE_VERSION. */
  isTestProfile?: boolean;

  // Profile summary (generated by PE-002 or PE-004)
  summary: ProfileSummary | null;
}

export interface ProfileSummary {
  summaryText: string;           // Natural language summary (2-3 sentences)
  upgradeHook: string | null;    // Only for quick diagnostic
  highlightDimensions: Array<{
    dimension: string;
    highlightType: 'strength' | 'notable';
    displayValue: string;
  }>;
  descriptiveLabel: string;      // e.g., "Visual-first deep learner with a top-down approach"
}

// Dimension display metadata
export interface DimensionDisplayInfo {
  key: string;
  name: string;
  colour: string;         // Brand Guide hex
  tier: 'free' | 'paid';
  description: string;    // User-facing description
}

export const DIMENSION_DISPLAY: DimensionDisplayInfo[] = [
  { key: 'contentFormat', name: 'Content Format', colour: '#6C5CE7', tier: 'free', description: 'How you prefer to absorb information' },
  { key: 'learningApproach', name: 'Learning Approach', colour: '#0ABFBC', tier: 'free', description: 'Whether you start with theory or dive into practice' },
  { key: 'depthTolerance', name: 'Depth Tolerance', colour: '#F0A500', tier: 'free', description: 'How deeply you need to understand material' },
  { key: 'sequencing', name: 'Sequencing', colour: '#55EFC4', tier: 'free', description: 'How you prefer to navigate through content' },
  { key: 'infoDensity', name: 'Information Density', colour: '#FDCB6E', tier: 'paid', description: 'How much information you process comfortably at once' },
  { key: 'motivation', name: 'Motivational Driver', colour: '#A594F9', tier: 'paid', description: 'What drives you to keep learning' },
  { key: 'viewpointRange', name: 'Viewpoint Range', colour: '#FD79A8', tier: 'paid', description: 'Whether you want alternative perspectives by default' },
  { key: 'priorKnowledge', name: 'Prior Knowledge', colour: '#74B9FF', tier: 'paid', description: 'Your existing expertise in the subject (assessed per upload)' },
];

// Category labels now come from the confidence-band tables rather than from hard
// cutoffs — a score in a transition zone gets a compound label ("Low–Medium")
// instead of being forced into a category it does not clearly belong to.
// See shared/src/constants/confidence-bands.ts. Intent decision 3.

export const getDepthCategory = (score: number): string =>
  bandLabel(DEPTH_TOLERANCE_BANDS, score);

export const getInfoDensityCategory = (score: number): string =>
  bandLabel(LOW_MEDIUM_HIGH_BANDS, score);

export const getViewpointCategory = (score: number): string =>
  bandLabel(LOW_MEDIUM_HIGH_BANDS, score);

/**
 * The Kolb-style quadrant label. Both axes must resolve clearly for a quadrant
 * to mean anything — if either sits in the balanced zone (41–59) the quadrant is
 * an artefact of rounding, so we say 'Balanced' rather than pick one.
 */
export const getLearningApproachLabel = (approach: LearningApproach): string => {
  const { theoryApplication, reflectiveActive } = approach;
  if (isLearningAxisBalanced(theoryApplication) || isLearningAxisBalanced(reflectiveActive)) {
    return 'Balanced';
  }
  const theoryFirst = theoryApplication > 50;
  const reflective = reflectiveActive > 50;
  if (theoryFirst && reflective) return 'Assimilating';
  if (theoryFirst && !reflective) return 'Converging';
  if (!theoryFirst && reflective) return 'Diverging';
  return 'Accommodating';
};

/** True when a Depth Tolerance score sits in a transition zone. */
export const isDepthUncertain = (score: number): boolean =>
  isUncertain(DEPTH_TOLERANCE_BANDS, score);

/** True when an Information Density or Viewpoint Range score sits in a transition zone. */
export const isLowMediumHighUncertain = (score: number): boolean =>
  isUncertain(LOW_MEDIUM_HIGH_BANDS, score);
